import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { cloneDeep } from 'lodash';
import { RvButtonComponent } from 'src/app/components/button/button.component';
import { CapAlertsService } from '../../../services/integrations/component-integrations/cap-alerts/cap-alerts.service';
import { CapAlertFilters } from '../../../types/cap-alerts-page.types';

export type CapAlertFiltersDialogData = { filters: CapAlertFilters };

type CsvFilterField =
  | 'allowedSenders'
  | 'allowedHandlingCodes'
  | 'allowedEventCodes';
type CheckboxFilterField = Exclude<keyof CapAlertFilters, CsvFilterField>;

type CheckboxGroup = {
  field: CheckboxFilterField;
  legend: string;
  idPrefix: string;
  options: { key: string; label: string }[];
};

/** Same rule as today's CSV fields: trim, split on commas, an empty field is an empty list. */
function parseCsvList(csv: string): string[] {
  return csv.trim() === '' ? [] : csv.split(',').map((item) => item.trim());
}

/**
 * Edits a copy of the filter lists. Save closes with the edited lists, Cancel
 * closes with nothing; the page decides what to do with them.
 */
@Component({
  selector: 'cap-alert-filters-dialog',
  templateUrl: './cap-alert-filters-dialog.component.html',
  styleUrl: './cap-alert-filters-dialog.component.scss',
  imports: [
    ReactiveFormsModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    RvButtonComponent,
  ],
})
export class CapAlertFiltersDialogComponent {
  private readonly dialogRef =
    inject<
      MatDialogRef<CapAlertFiltersDialogComponent, CapAlertFilters | null>
    >(MatDialogRef);
  private readonly data = inject<CapAlertFiltersDialogData>(MAT_DIALOG_DATA);
  private readonly capAlertsService = inject(CapAlertsService);

  private readonly original: CapAlertFilters = cloneDeep(this.data.filters);

  protected readonly form = new FormGroup({
    allowedSenders: new FormControl(this.csvOf('allowedSenders'), {
      nonNullable: true,
    }),
    allowedHandlingCodes: new FormControl(this.csvOf('allowedHandlingCodes'), {
      nonNullable: true,
    }),
    allowedEventCodes: new FormControl(this.csvOf('allowedEventCodes'), {
      nonNullable: true,
    }),
  });

  protected readonly csvFields: {
    field: CsvFilterField;
    label: string;
  }[] = [
    { field: 'allowedSenders', label: 'Sender' },
    { field: 'allowedHandlingCodes', label: 'Handling' },
    { field: 'allowedEventCodes', label: 'Code' },
  ];

  protected readonly groups: CheckboxGroup[] = [
    {
      field: 'allowedStatuses',
      legend: 'Status',
      idPrefix: 'statusesCheckbox',
      options: this.capAlertsService.allowedStatusesValues,
    },
    {
      field: 'allowedUrgencies',
      legend: 'Urgency',
      idPrefix: 'urgenciesCheckbox',
      options: this.capAlertsService.allowedUrgenciesValues,
    },
    {
      field: 'allowedSeverities',
      legend: 'Severity',
      idPrefix: 'severitiesCheckbox',
      options: this.capAlertsService.allowedSeveritiesValues,
    },
    {
      field: 'allowedCertainties',
      legend: 'Certainty',
      idPrefix: 'certaintiesCheckbox',
      options: this.capAlertsService.allowedCertaintiesValues,
    },
    {
      field: 'allowedCategories',
      legend: 'Category',
      idPrefix: 'categoriesCheckbox',
      options: this.capAlertsService.allowedCategoriesValues,
    },
  ];

  private readonly selections = signal<Record<CheckboxFilterField, string[]>>({
    allowedStatuses: [...(this.original.allowedStatuses ?? [])],
    allowedUrgencies: [...(this.original.allowedUrgencies ?? [])],
    allowedSeverities: [...(this.original.allowedSeverities ?? [])],
    allowedCertainties: [...(this.original.allowedCertainties ?? [])],
    allowedCategories: [...(this.original.allowedCategories ?? [])],
  });

  protected isChecked(field: CheckboxFilterField, key: string): boolean {
    return this.selections()[field].includes(key);
  }

  protected setChecked(
    field: CheckboxFilterField,
    key: string,
    checked: boolean,
  ): void {
    const current = this.selections()[field];
    const next = checked
      ? current.includes(key)
        ? current
        : [...current, key]
      : current.filter((item) => item !== key);
    this.selections.update((all) => ({ ...all, [field]: next }));
  }

  protected save(): void {
    this.dialogRef.close(this.result());
  }

  protected cancel(): void {
    this.dialogRef.close(null);
  }

  private result(): CapAlertFilters {
    return {
      ...this.original,
      // A list nobody touched keeps its exact stored items.
      ...Object.fromEntries(
        this.csvFields.map(({ field }) => [
          field,
          this.form.controls[field].dirty
            ? parseCsvList(this.form.controls[field].value)
            : (this.original[field] ?? []),
        ]),
      ),
      ...this.selections(),
    };
  }

  private csvOf(field: CsvFilterField): string {
    return (this.original[field] ?? []).join(',');
  }
}
